import React from 'react';
import * as d3 from 'd3';
import Tooltip from './Tooltip';
import { PeriodType } from '../performanceConstants';
import { FONT_BLACK } from '../../../../../colors';
import './performance-charts.scss';

const MARGIN = {
  TOP: 10,
  RIGHT: 10,
  LEFT: 0,
  BOTTOM: 10,
};

const BAR_HEIGHT = 25;
const BAR_SPACING = 20;

const LABEL_LINE_HEIGHT = 16;
const LABEL_GAP = 3;

const getBarColor = period => {
  switch (period) {
    case PeriodType.CURRENT:
      return '#64f884';
    case PeriodType.LAST:
      return '#0d729e';
    case PeriodType.STLY:
      return '#888888';
    default:
      return '#64f884';
  }
};

/**
 * Creates hatch patterns for each forecast bar.
 * Each pattern uses the given color.
 *
 * @param {Object} svg - The D3 selection of the SVG element.
 * @param {Array} data - The data array (length should be 3).
 */
const createHatchPatterns = (svg, data) => {
  const defs = svg.append('defs');
  data.forEach((d, i) => {
    defs
      .append('pattern')
      .attr('id', `hatch-${i}`)
      .attr('patternUnits', 'userSpaceOnUse')
      .attr('width', 4)
      .attr('height', 4)
      .append('path')
      .attr('d', 'M0,0 l4,4')
      .attr('stroke', getBarColor(d.periodType))
      .attr('stroke-width', 1);
  });
};

/**
 * Draws the vertical stacked bars.
 * For each bar, two rectangles are drawn:
 *  - The current portion (solid fill).
 *  - The forecast portion (filled with a hatch pattern).
 *
 * @param {Object} g - The D3 selection of the main group.
 * @param {Object} xScale - The x band scale.
 * @param {Object} yScale - The y linear scale.
 * @param {Array} data - The data array (expected length is 3).
 */
const drawBars = (
  g,
  xScale,
  yScale,
  data,
  numberFormatter,
  setTooltipContent,
  setTooltipPosition,
  innerWidth,
  reservedRight,
  measureText,
) => {
  const handleMouseOver = (content, x, y) => {
    setTooltipPosition({ x, y });
    setTooltipContent(content);
  };
  const handleMouseOut = () => setTooltipContent('');

  data.forEach((d, i) => {
    const y = yScale(i);
    const barH = BAR_HEIGHT;

    const currentValue = d.current;
    const forecastValue = d.forecast;
    const totalValue = currentValue + forecastValue;

    const currentW = xScale(currentValue);
    const totalW = xScale(totalValue);
    const forecastW = totalW - currentW;

    const labelText = numberFormatter(totalValue);
    const labelW = measureText(labelText);
    const leftX = Math.min(totalW, innerWidth + reservedRight - labelW);

    g.append('rect')
      .attr('x', 0)
      .attr('y', y)
      .attr('width', currentW)
      .attr('height', barH)
      .attr('fill', getBarColor(d.periodType));

    g.append('rect')
      .attr('x', currentW)
      .attr('y', y)
      .attr('width', Math.max(0, forecastW))
      .attr('height', barH)
      .attr('fill', `url(#hatch-${i})`)
      .on('mouseover', () => {
        if (d.forecastTooltip && forecastW > 0) {
          const tooltipX = currentW + forecastW / 2;
          const tooltipY = y + barH / 2;
          handleMouseOver(d.forecastTooltip, tooltipX, tooltipY);
        }
      })
      .on('mouseout', handleMouseOut);

    g.append('rect')
      .attr('x', 0)
      .attr('y', y)
      .attr('width', currentW)
      .attr('height', barH)
      .attr('fill', 'transparent')
      .style('pointer-events', 'all')
      .on('mouseover', () => {
        if (d.currentTooltip && currentW > 0) {
          const tooltipX = currentW / 2;
          const tooltipY = y + barH / 2;
          handleMouseOver(d.currentTooltip, tooltipX, tooltipY);
        }
      })
      .on('mouseout', handleMouseOut);

    const textElement = g
      .append('text')
      .attr('x', leftX)
      .attr('dx', '0.3em')
      .attr('y', y + barH / 2)
      .attr('dy', '0.35em')
      .attr('text-anchor', 'start')
      .attr('fill', FONT_BLACK)
      .style('font-weight', '500')
      .style('font-size', '14px')
      .text(labelText);

    const bbox = textElement.node().getBBox();
    g.insert('rect', () => textElement.node())
      .attr('x', bbox.x - 2)
      .attr('y', bbox.y)
      .attr('width', bbox.width + 4)
      .attr('height', bbox.height)
      .attr('fill', 'white');
  });
};

/**
 * VerticalBarChart component renders a vertical stacked bar chart using D3.
 * Each object represents a bar and must contain:
 * - current: numeric value for the current period.
 * - forecast: numeric value for the forecast.
 * - currentTooltip: string tooltip for the current portion.
 * - forecastTooltip: string tooltip for the forecast portion.
 * - label: string to use for the label underneath the bar
 *
 * The forecast portion is drawn with a hatch pattern of the same color as the solid bar.
 *
 * @param {Object} props
 * @param {Array<Object>} props.data - Array of 3 data objects.
 * @param {number} [props.width=500] - Width of the chart.
 * @param {number} [props.height=300] - Height of the chart.
 * @returns {JSX.Element} The rendered chart.
 */
const PerformanceBarChart = ({ data, numberFormatter, width, height }) => {
  const svgRef = React.useRef();
  const tooltipRef = React.useRef(null);
  const [tooltipContent, setTooltipContent] = React.useState('');
  const [tooltipPosition, setTooltipPosition] = React.useState({ x: 0, y: 0 });

  React.useLayoutEffect(() => {
    const svg = d3.select(svgRef.current);
    // Clear previous content
    svg.selectAll('*').remove();

    // Set margins and dimensions
    const measureText = (() => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      ctx.font = 'bold 12px URWDIN, sans-serif';
      return t => ctx.measureText(String(t)).width;
    })();
    const formattedTotals = data.map(d => numberFormatter(d.current + d.forecast));
    const maxLabelWidth = formattedTotals.length ? Math.max(...formattedTotals.map(measureText)) : 0;
    const RIGHT_LABEL_PAD = 12;
    const reservedRight = Math.ceil(maxLabelWidth) + RIGHT_LABEL_PAD;

    const innerWidth = Math.max(0, width - MARGIN.LEFT - MARGIN.RIGHT - reservedRight);

    const rowStep = BAR_HEIGHT + BAR_SPACING;

    // Create a group for the chart contents
    const g = svg
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${MARGIN.LEFT},${MARGIN.TOP})`);

    // Define y scale (a band scale for 3 bars)
    const yScale = i => i * rowStep;

    // Define x scale based on the maximum stacked value
    const maxTotal = d3.max(data, d => d.current + d.forecast) || 0;
    const xScale = d3
      .scaleLinear()
      .domain([0, maxTotal])
      .range([0, innerWidth])
      .nice();

    // Create the hatch patterns for forecast fills
    createHatchPatterns(svg, data);

    // Draw the stacked bars (current + forecast) with tooltip events
    drawBars(
      g,
      xScale,
      yScale,
      data,
      numberFormatter,
      setTooltipContent,
      setTooltipPosition,
      innerWidth,
      reservedRight,
      measureText,
    );

    // Draw labels below each bar
    data.forEach((d, i) => {
      g.append('text')
        .attr('x', 0)
        .attr('y', yScale(i) + BAR_HEIGHT + LABEL_GAP + LABEL_LINE_HEIGHT / 2)
        .attr('text-anchor', 'start')
        .attr('fill', FONT_BLACK)
        .style('font-weight', '500')
        .attr('font-size', '10px')
        .text(d.label);
    });
  }, [data, numberFormatter, width, height]);

  return (
    <div style={{ position: 'relative', overflow: 'visible' }}>
      <svg ref={svgRef} width={width} height={height} />
      {tooltipContent && <Tooltip ref={tooltipRef} {...tooltipPosition} content={tooltipContent} transform />}
    </div>
  );
};

export default PerformanceBarChart;
